import React, { useEffect, useRef, useState } from 'react';

// Main App Component
function App() {
  // State to manage the current "page" being displayed
  const [currentPage, setCurrentPage] = useState('home');
  // State to manage the visibility of the mobile menu
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // --- Data Definitions ---
  // In a real application, this data would likely come from an API, database, or CMS.

  const clientBrands = [
    { id: 1, name: 'Global Corp', logoUrl: 'https://placehold.co/100x50/F1F5F9/64748B?text=Client+A' }, // Adjusted for white theme
    { id: 2, name: 'Tech Innovators', logoUrl: 'https://placehold.co/100x50/F1F5F9/64748B?text=Client+B' }, // Adjusted for white theme
    { id: 3, name: 'Finance Solutions', logoUrl: 'https://placehold.co/100x50/F1F5F9/64748B?text=Client+C' }, // Adjusted for white theme
    { id: 4, name: 'Healthcare United', logoUrl: 'https://placehold.co/100x50/F1F5F9/64748B?text=Client+D' }, // Adjusted for white theme
    { id: 5, name: 'Retail Giant', logoUrl: 'https://placehold.co/100x50/F1F5F9/64748B?text=Client+E' }, // Adjusted for white theme
  ];

  const testimonials = [
    {
      id: 1,
      quote: "ThrySec's SOC services have significantly enhanced our cybersecurity posture. Their 24/7 monitoring and rapid response are invaluable.",
      author: 'Jane Doe',
      title: 'CTO, Tech Solutions Inc.',
      avatarUrl: 'https://placehold.co/80x80/CBD5E1/1E293B?text=JD' // Adjusted for white theme
    },
    {
      id: 2,
      quote: "We feel much more secure knowing that a team of experts is constantly watching over our systems. Highly recommend their proactive approach.",
      author: 'John Smith',
      title: 'CEO, Global Enterprises',
      avatarUrl: 'https://placehold.co/80x80/CBD5E1/1E293B?text=JS' // Adjusted for white theme
    },
    {
      id: 3,
      quote: "The detailed reports and clear communication from ThrySec have made understanding our security landscape so much easier.",
      author: 'Emily White',
      title: 'Security Lead, Innovate Corp',
      avatarUrl: 'https://placehold.co/80x80/CBD5E1/1E293B?text=EW' // Adjusted for white theme
    },
  ];

  const teamMembers = [
    {
      id: 1,
      name: 'Michael Chen',
      role: 'Chief Security Officer',
      bio: 'With over 20 years in cybersecurity, Michael leads our strategic initiatives and ensures top-tier service delivery.',
      imageUrl: 'https://placehold.co/150x150/CBD5E1/1E293B?text=MC', // Adjusted for white theme
      linkedin: '#',
      twitter: '#'
    },
    {
      id: 2,
      name: 'Sarah Lee',
      role: 'Lead SOC Analyst',
      bio: 'Sarah is an expert in threat detection and incident response, leading our 24/7 monitoring team.',
      imageUrl: 'https://placehold.co/150x150/CBD5E1/1E293B?text=SL', // Adjusted for white theme
      linkedin: '#',
      twitter: '#'
    },
    {
      id: 3,
      name: 'David Kim',
      role: 'Vulnerability Management Specialist',
      bio: 'David specializes in identifying and mitigating vulnerabilities, helping clients strengthen their defenses.',
      imageUrl: 'https://placehold.co/150x150/CBD5E1/1E293B?text=DK', // Adjusted for white theme
      linkedin: '#',
      twitter: '#'
    },
  ];

  // --- Reusable Components ---

 ;

  const GradientText = ({ children, className = '' }) => (
    <span className={`bg-gradient-to-r from-thrysec-blue via-thrysec-orange to-thrysec-blue text-transparent bg-clip-text ${className}`}>
      {children}
    </span>
  );

  // TypewriterText component with startDelay
  const TypewriterText = ({ text, delay = 70, startDelay = 0 }) => {
    const [currentText, setCurrentText] = useState('');
    const [currentIndex, setCurrentIndex] = useState(0);
    const [animationStarted, setAnimationStarted] = useState(false);

    useEffect(() => {
      let initialTimeout;
      if (startDelay > 0 && !animationStarted) {
        initialTimeout = setTimeout(() => {
          setAnimationStarted(true);
        }, startDelay);
      } else {
        setAnimationStarted(true);
      }

      return () => clearTimeout(initialTimeout);
    }, [startDelay, animationStarted]);

    useEffect(() => {
      let timeout;
      if (animationStarted && currentIndex < text.length) {
        timeout = setTimeout(() => {
          setCurrentText(prevText => prevText + text[currentIndex]);
          setCurrentIndex(prevIndex => prevIndex + 1);
        }, delay);
      }

      return () => clearTimeout(timeout);
    }, [currentIndex, delay, text, animationStarted]);

    return (
      <span className="typewriter-effect">
        <GradientText>{currentText}</GradientText>
        {/* Only show blinking caret if animation is still ongoing for this text */}
        {animationStarted && currentIndex < text.length && (
          <span className="typing-caret" style={{ borderColor: 'thrysec-blue' }}></span> // Caret color from logo blue
        )}
      </span>
    );
  };


  const ClientLogo = ({ logoUrl, name }) => (
    <div className="flex items-center justify-center p-4 bg-white rounded-lg shadow-xl hover:shadow-2xl transition duration-300 ease-in-out transform hover:scale-105 border border-gray-200"> {/* Adjusted for white theme */}
      <img src={logoUrl} alt={`${name} Logo`} className="max-h-12 object-contain filter grayscale hover:grayscale-0 transition duration-300" onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/100x50/F1F5F9/64748B?text=Logo'; }} /> {/* Adjusted for white theme */}
    </div>
  );

  const TestimonialCard = ({ quote, author, title, avatarUrl }) => (
    <div className="bg-white p-8 rounded-xl shadow-2xl border border-gray-200 flex flex-col items-center text-center relative overflow-hidden group hover:scale-105 transition duration-300 ease-in-out"> {/* Adjusted for white theme */}
      {/* Subtle gradient border on hover */}
      <div className="absolute inset-0 rounded-xl border-2 border-transparent group-hover:border-thrysec-blue group-hover:border-opacity-50 transition-all duration-300 ease-in-out"></div>
      <img src={avatarUrl} alt={author} className="w-20 h-20 rounded-full mb-4 object-cover border-2 border-thrysec-blue group-hover:border-thrysec-orange transition duration-300" onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/80x80/CBD5E1/1E293B?text=Avatar'; }} /> {/* Adjusted for white theme */}
      <p className="text-lg text-slate-700 italic mb-4">"{quote}"</p> {/* Adjusted for white theme */}
      <p className="font-semibold text-slate-900">{author}</p> {/* Adjusted for white theme */}
      <p className="text-sm text-slate-600">{title}</p> {/* Adjusted for white theme */}
    </div>
  );

  const TeamMemberCard = ({ name, role, bio, imageUrl, linkedin, twitter }) => (
    <div className="bg-white p-8 rounded-xl shadow-2xl border border-gray-200 flex flex-col items-center text-center relative overflow-hidden group hover:scale-105 transition duration-300 ease-in-out"> {/* Adjusted for white theme */}
      {/* Subtle gradient border on hover */}
      <div className="absolute inset-0 rounded-xl border-2 border-transparent group-hover:border-thrysec-blue group-hover:border-opacity-50 transition-all duration-300 ease-in-out"></div>
      <img src={imageUrl} alt={name} className="w-32 h-32 rounded-full mb-4 object-cover border-2 border-thrysec-blue group-hover:border-thrysec-orange transition duration-300" onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/150x150/CBD5E1/1E293B?text=Profile'; }} /> {/* Adjusted for white theme */}
      <h3 className="text-2xl font-semibold text-slate-900 mb-2">{name}</h3> {/* Adjusted for white theme */}
      <p className={`text-thrysec-blue mb-3`}>{role}</p> {/* Adjusted for white theme */}
      <p className="text-slate-700 text-sm mb-4">{bio}</p> {/* Adjusted for white theme */}
      <div className="flex space-x-4">
        {linkedin && (
          <a href={linkedin} target="_blank" rel="noopener noreferrer" className={`text-slate-700 hover:text-thrysec-blue transition duration-300`}> {/* Adjusted for white theme */}
            {/* LinkedIn Icon - Inline SVG for simplicity */}
            <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path fillRule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clipRule="evenodd"></path></svg>
          </a>
        )}
        {twitter && (
          <a href={twitter} target="_blank" rel="noopener noreferrer" className={`text-slate-700 hover:text-thrysec-blue transition duration-300`}> {/* Adjusted for white theme */}
            {/* Twitter Icon - Inline SVG for simplicity */}
            <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.007-.532A8.318 8.318 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.222 8.222 0 01-2.606.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012 10.702v.053a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84"></path></svg>
          </a>
        )}
      </div>
    </div>
  );

  // --- Canvas Animation Logic ---
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return; // Exit if canvas is not available
    const ctx = canvas.getContext('2d');
    let animationFrameId;

    // Set canvas dimensions to fill parent
    const setCanvasSize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    setCanvasSize(); // Initial size set
    window.addEventListener('resize', setCanvasSize); // Adjust on resize

    // Particle properties
    const particles = [];
    const numParticles = 200; // Increased density for more "network" feel
    const maxDistance = 120; // Max distance for lines to connect

    // Create particles
    for (let i = 0; i < numParticles; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 2, // Slightly faster movement
        vy: (Math.random() - 0.5) * 2, // Slightly faster movement
        radius: Math.random() * 1.2 + 0.8, // Slightly larger particles for more visibility
        color: `rgba(43, 76, 186, ${Math.random() * 0.2 + 0.1})` // Subtle blue particles, more opaque
      });
    }

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height); // Clear canvas

      particles.forEach(p1 => {
        // Update particle position
        p1.x += p1.vx;
        p1.y += p1.vy;

        // Bounce off edges
        if (p1.x < 0 || p1.x > canvas.width) p1.vx *= -1;
        if (p1.y < 0 || p1.y > canvas.height) p1.vy *= -1;

        // Draw particle
        ctx.beginPath();
        ctx.arc(p1.x, p1.y, p1.radius, 0, Math.PI * 2);
        ctx.fillStyle = p1.color;
        ctx.fill();

        // Draw lines to nearby particles
        particles.forEach(p2 => {
          const distance = Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2));
          if (distance < maxDistance) {
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            // Line opacity based on distance, making them more visible
            ctx.strokeStyle = `rgba(43, 76, 186, ${0.8 - (distance / maxDistance) * 0.6})`; // More opaque blue lines
            ctx.lineWidth = 1; // Thicker lines for a "circuit" feel
            ctx.stroke();
          }
        });
      });

      animationFrameId = requestAnimationFrame(animate); // Loop animation
    };

    animate(); // Start animation

    // Cleanup on component unmount
    return () => {
      window.removeEventListener('resize', setCanvasSize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []); // Empty dependency array ensures this runs once on mount

  // Function to toggle the mobile menu
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Function to handle navigation and close the menu
  const navigateTo = (pageId, sectionId = null) => {
    setCurrentPage(pageId);
    setIsMenuOpen(false); // Close the menu on navigation
    // If navigating to the home page, scroll to the specific section if provided
    if (pageId === 'home' && sectionId) {
      // Use a timeout to allow the page to render before scrolling
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      // For other pages (like privacy or terms), scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };


  // Privacy Policy Page Component (inlined for single file)
  const PrivacyPolicyPage = () => {
    return (
      <section className="py-20 px-6 bg-gray-50 section-pattern min-h-screen"> {/* Adjusted for white theme */}
        <div className="container mx-auto max-w-4xl text-slate-800 text-left"> {/* Adjusted for white theme */}
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
            <GradientText>Privacy Policy</GradientText>
          </h2>
          <p className="mb-4">
            This Privacy Policy describes how ThrySec ("we," "us," or "our") collects, uses, and discloses your personal information when you visit, use, or make a purchase from our website (the "Site") or otherwise communicate with us.
          </p>
          <h3 className="text-2xl font-semibold mt-8 mb-4">Information We Collect</h3>
          <p className="mb-4">
            We collect various types of information in connection with the services we provide, including:
          </p>
          <ul className="list-disc list-inside ml-4 mb-4">
            <li>**Personal Information:** Such as your name, email address, phone number, and company details when you fill out contact forms or schedule meetings.</li>
            <li>**Usage Data:** Information about how you access and use the Site, including your IP address, browser type, operating system, referral URLs, pages viewed, and the dates/times of your visits.</li>
            <li>**Cookies and Tracking Technologies:** We use cookies and similar tracking technologies to track activity on our Site and hold certain information.</li>
          </ul>
          <h3 className="text-2xl font-semibold mt-8 mb-4">How We Use Your Information</h3>
          <p className="mb-4">
            We use the information we collect for various purposes, including to:
          </p>
          <ul className="list-disc list-inside ml-4 mb-4">
            <li>Provide, operate, and maintain our services.</li>
            <li>Improve, personalize, and expand our services.</li>
            <li>Understand and analyze how you use our services.</li>
            <li>Communicate with you, either directly or through one of our partners, including for customer service, to provide you with updates and other information relating to the service, and for marketing and promotional purposes.</li>
            <li>Process your inquiries and requests.</li>
            <li>Detect and prevent fraud.</li>
          </ul>
          <h3 className="text-2xl font-semibold mt-8 mb-4">Sharing Your Information</h3>
          <p className="mb-4">
            We may share your personal information with third parties in the following situations:
          </p>
          <ul className="list-disc list-inside ml-4 mb-4">
            <li>**Service Providers:** With vendors, consultants, and other service providers who perform services on our behalf.</li>
            <li>**Business Transfers:** In connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business by another company.</li>
            <li>**Legal Requirements:** If required to do so by law or in response to valid requests by public authorities (e.g., a court or a government agency).</li>
          </ul>
          <h3 className="text-2xl font-semibold mt-8 mb-4">Your Data Protection Rights</h3>
          <p className="mb-4">
            Depending on your location, you may have the following rights regarding your personal data:
          </p>
          <ul className="list-disc list-inside ml-4 mb-4">
            <li>The right to access, update, or delete the information we have on you.</li>
            <li>The right to rectify any inaccurate information.</li>
            <li>The right to object to our processing of your personal data.</li>
            <li>The right to request the restriction of the processing of your personal data.</li>
            <li>The right to data portability.</li>
            <li>The right to withdraw consent.</li>
          </ul>
          <p className="mb-4">
            To exercise any of these rights, please contact us at <a href="mailto:security@thrysec.com" className={`text-thrysec-blue hover:underline`}>security@thrysec.com</a>. {/* Adjusted for white theme */}
          </p>
          <h3 className="text-2xl font-semibold mt-8 mb-4">Changes to This Privacy Policy</h3>
          <p className="mb-4">
            We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page. You are advised to review this Privacy Policy periodically for any changes.
          </p>
          <p className="text-sm text-slate-600 mt-8"> {/* Adjusted for white theme */}
            Last updated: August 5, 2025
          </p>
        </div>
      </section>
    );
  };

  // Terms of Service Page Component (inlined for single file)
  const TermsOfServicePage = () => {
    return (
      <section className="py-20 px-6 bg-gray-50 section-pattern min-h-screen"> {/* Adjusted for white theme */}
        <div className="container mx-auto max-w-4xl text-slate-800 text-left"> {/* Adjusted for white theme */}
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
            <GradientText>Terms of Service</GradientText>
          </h2>
          <p className="mb-4">
            Welcome to ThrySec! These Terms of Service ("Terms") govern your use of the ThrySec website and services (collectively, the "Services"). By accessing or using our Services, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Services.
          </p>

          <h3 className="text-2xl font-semibold mt-8 mb-4">1. Acceptance of Terms</h3>
          <p className="mb-4">
            By creating an account or using the Services, you affirm that you are of legal age to enter into this agreement, or, if you are not, that you have obtained parental or guardian consent to enter into this agreement.
          </p>

          <h3 className="text-2xl font-semibold mt-8 mb-4">2. Services Description</h3>
          <p className="mb-4">
            ThrySec provides cybersecurity services, including SOC monitoring, threat detection, incident response, and vulnerability management. The specifics of the services provided will be outlined in a separate agreement between ThrySec and the client.
          </p>

          <h3 className="text-2xl font-semibold mt-8 mb-4">3. User Responsibilities</h3>
          <p className="mb-4">
            As a user of our Services, you agree to:
          </p>
          <ul className="list-disc list-inside ml-4 mb-4">
            <li>Provide accurate and complete information when requested.</li>
            <li>Maintain the confidentiality of any account credentials.</li>
            <li>Use the Services only for lawful purposes and in accordance with these Terms.</li>
            <li>Not engage in any activity that interferes with or disrupts the Services.</li>
          </ul>

          <h3 className="text-2xl font-semibold mt-8 mb-4">4. Intellectual Property</h3>
          <p className="mb-4">
            All content, trademarks, service marks, trade names, and logos on the Site are proprietary to ThrySec or its licensors and are protected by intellectual property laws. You may not use any of our intellectual property without our prior written consent.
          </p>

          <h3 className="text-2xl font-semibold mt-8 mb-4">5. Disclaimers</h3>
          <p className="mb-4">
            The Services are provided "as is" and "as available" without any warranties of any kind, either express or implied, including, but not limited to, implied warranties of merchantability, fitness for a particular purpose, or non-infringement. ThrySec does not warrant that the Services will be uninterrupted, secure, or error-free.
          </p>

          <h3 className="text-2xl font-semibold mt-8 mb-4">6. Limitation of Liability</h3>
          <p className="mb-4">
            To the fullest extent permitted by law, ThrySec shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses, resulting from (a) your access to or use of or inability to access or use the Services; (b) any conduct or content of any third party on the Services; or (c) unauthorized access, use, or alteration of your transmissions or content.
          </p>

          <h3 className="text-2xl font-semibold mt-8 mb-4">7. Governing Law</h3>
          <p className="mb-4">
            These Terms shall be governed and construed in accordance with the laws of Malaysia, without regard to its conflict of law provision.
          </p>

          <h3 className="text-2xl font-semibold mt-8 mb-4">8. Changes to Terms</h3>
          <p className="mb-4">
            We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
          </p>

          <h3 className="text-2xl font-semibold mt-8 mb-4">9. Contact Us</h3>
          <p className="mb-4">
            If you have any questions about these Terms, please contact us at <a href="mailto:security@thrysec.com" className={`text-thrysec-blue hover:underline`}>security@thrysec.com</a>. {/* Adjusted for white theme */}
          </p>
          <p className="text-sm text-slate-600 mt-8"> {/* Adjusted for white theme */}
            Last updated: August 5, 2025
          </p>
        </div>
      </section>
    );
  };


  // --- Main Render ---
  return (
    <div className="font-inter antialiased bg-gray-50 text-slate-900 min-h-screen"> {/* Adjusted for white theme */}
      {/* Custom styles for gradient text and scrollbar */}
      <style>{`
        /* Import Space Mono from Google Fonts */
        @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&display=swap');

        body {
            font-family: 'Inter', sans-serif; /* Default body font for readability */
            background-color: #F8FAFC; /* Light background */
            color: #1E293B; /* Dark text */
        }
        /* Apply Space Mono to headings and the logo */
        h1, h2, h3, .logo-text, .nav-link {
            font-family: 'Space Mono', monospace;
        }

        /* Cybersecurity Image Background for Hero Section */
        .hero-bg {
            /* Removed background-image, now handled by canvas */
            background-color: #F1F5F9; /* Lighter fallback/base color */
            position: relative; /* Needed for canvas absolute positioning */
        }
        .hero-canvas {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 0; /* Ensure canvas is behind content */
            opacity: 0.3; /* Increased opacity for more visibility */
        }
        /* Subtle pattern for other sections */
        .section-pattern {
            background-image: url("data:image/svg+xml,%3Csvg width='6' height='6' viewBox='0 0 6 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%2364748B' fill-opacity='0.05' fill-rule='evenodd'%3E%3Cpath d='M5 0h1L0 6V5zm1 6v-1L1 0h1z'/%3E%3C/g%3E%3C/svg%3E"); /* Darker pattern for light background */
        }

        /* Ensure smooth scrolling for anchor links */
        html {
            scroll-behavior: smooth;
        }
        /* Custom scrollbar for a sleek look */
        ::-webkit-scrollbar {
            width: 8px;
        }
        ::-webkit-scrollbar-track {
            background: #E2E8F0; /* Lighter track */
            border-radius: 10px;
        }
        ::-webkit-scrollbar-thumb {
            background: #94A3B8; /* Lighter slate thumb */
            border-radius: 10px;
        }
        ::-webkit-scrollbar-thumb:hover {
            background: #3B82F6; /* Blue hover */
        }

        /* Keyframe animations for blob effect */
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite cubic-bezier(0.6, 0.01, 0.3, 0.9);
        }
        .animation-delay-2000 {
            animation-delay: 2s;
        }
        .animation-delay-4000 {
            animation-delay: 4s;
        }

        /* Typewriter effect for the H1 text */
        /* Updated to support individual lines */
        .typewriter-effect {
          display: inline-block; /* Essential for the caret to work correctly */
          overflow: hidden; /* Ensures text is hidden until typed */
          white-space: nowrap; /* Keeps text on a single line */
        }

        .typing-caret {
            border-right: 0.15em solid; /* Blinking caret, color set by inline style */
            animation: blink-caret 0.75s step-end infinite; /* Blinking caret animation */
            display: inline-block; /* Ensure caret is visible with text */
            height: 1em; /* Adjust height to match line height */
            vertical-align: middle; /* Align with text */
        }

        @keyframes typing {
          from { width: 0 }
          to { width: 100% }
        }

        @keyframes blink-caret {
          from, to { border-color: transparent }
          50% { border-color: currentColor; } /* Use current text color for caret */
        }

        /* Scanline Overlay for a "tacky" digital feel */
        .scanline-overlay {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 1; /* Above canvas, below text */
            background: repeating-linear-gradient(
                to bottom,
                rgba(0, 0, 0, 0.05),
                rgba(0, 0, 0, 0.05) 1px,
                transparent 1px,
                transparent 2px
            );
            animation: scanlines 8s linear infinite;
        }

        @keyframes scanlines {
            from { background-position: 0 0; }
            to { background-position: 0 100%; }
        }
      `}</style>

      {/* Header/Navigation */}
      <header className="bg-white bg-opacity-90 backdrop-blur-md shadow-lg py-4 px-6 fixed w-full z-50 rounded-b-xl border-b border-blue-200"> {/* Adjusted for white theme */}
        <nav className="container mx-auto flex justify-between items-center relative">
          <a href="#home" onClick={() => navigateTo('home')} className="text-3xl font-extrabold rounded-md p-2">
            {/* Replaced GradientText with img for the logo */}
            <img src="/Footer Logo _bckdg.png" alt="ThrySec Logo" className="h-10 w-auto object-contain" />
          </a>
          {/* Desktop navigation */}
          <ul className="hidden md:flex space-x-6 text-sm font-semibold">
            <li><a href="#home" onClick={() => navigateTo('home', 'home')} className="nav-link text-slate-700 hover:text-slate-900 transition duration-300 ease-in-out rounded-full px-4 py-2 hover:bg-blue-100">Home</a></li> {/* Adjusted for white theme */}
            <li><a href="#services" onClick={() => navigateTo('home', 'services')} className="nav-link text-slate-700 hover:text-slate-900 transition duration-300 ease-in-out rounded-full px-4 py-2 hover:bg-blue-100">Services</a></li> {/* Adjusted for white theme */}
            <li><a href="#approach" onClick={() => navigateTo('home', 'approach')} className="nav-link text-slate-700 hover:text-slate-900 transition duration-300 ease-in-out rounded-full px-4 py-2 hover:bg-blue-100">Our Approach</a></li> {/* Adjusted for white theme */}
            <li><a href="#about" onClick={() => navigateTo('home', 'about')} className="nav-link text-slate-700 hover:text-slate-900 transition duration-300 ease-in-out rounded-full px-4 py-2 hover:bg-blue-100">About Us</a></li> {/* Adjusted for white theme */}
            <li><a href="#contact" onClick={() => navigateTo('home', 'contact')} className="nav-link text-slate-700 hover:text-slate-900 transition duration-300 ease-in-out rounded-full px-4 py-2 hover:bg-blue-100">Contact</a></li> {/* Adjusted for white theme */}
          </ul>
          {/* Hamburger menu for mobile */}
          <div className="md:hidden">
            <button onClick={toggleMenu} className="text-slate-700 hover:text-slate-900 focus:outline-none p-2 rounded-md hover:bg-gray-200 transition duration-300"> {/* Adjusted for white theme */}
              <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7"></path>
                )}
              </svg>
            </button>
          </div>
        </nav>
        {/* Mobile menu, conditionally rendered */}
        <div className={`md:hidden absolute top-full left-0 w-full bg-white bg-opacity-90 backdrop-blur-md shadow-lg border-t border-blue-200 overflow-hidden transition-all duration-300 ease-in-out ${isMenuOpen ? 'max-h-96 opacity-100 py-4' : 'max-h-0 opacity-0'}`}>
          <ul className="flex flex-col items-center space-y-4 text-sm font-semibold">
            <li><a href="#home" onClick={() => navigateTo('home', 'home')} className="nav-link text-slate-700 hover:text-slate-900 transition duration-300 ease-in-out rounded-full px-4 py-2 hover:bg-blue-100 w-full block text-center">Home</a></li>
            <li><a href="#services" onClick={() => navigateTo('home', 'services')} className="nav-link text-slate-700 hover:text-slate-900 transition duration-300 ease-in-out rounded-full px-4 py-2 hover:bg-blue-100 w-full block text-center">Services</a></li>
            <li><a href="#approach" onClick={() => navigateTo('home', 'approach')} className="nav-link text-slate-700 hover:text-slate-900 transition duration-300 ease-in-out rounded-full px-4 py-2 hover:bg-blue-100 w-full block text-center">Our Approach</a></li>
            <li><a href="#about" onClick={() => navigateTo('home', 'about')} className="nav-link text-slate-700 hover:text-slate-900 transition duration-300 ease-in-out rounded-full px-4 py-2 hover:bg-blue-100 w-full block text-center">About Us</a></li>
            <li><a href="#contact" onClick={() => navigateTo('home', 'contact')} className="nav-link text-slate-700 hover:text-slate-900 transition duration-300 ease-in-out rounded-full px-4 py-2 hover:bg-blue-100 w-full block text-center">Contact</a></li>
          </ul>
        </div>
      </header>

      {/* Added pt-20 to main to push content down below the fixed header */}
      <main className="pt-20">
        {currentPage === 'home' && (
          <>
            {/* Hero Section */}
            <section id="home" className="hero-bg relative h-screen flex items-center justify-center text-center p-4 overflow-hidden">
              {/* Canvas for dynamic background */}
              <canvas ref={canvasRef} className="hero-canvas"></canvas>
              <div className="absolute inset-0 bg-white opacity-75"></div> {/* Adjusted for white theme */}
              {/* Scanline Overlay */}
              <div className="scanline-overlay"></div>
              {/* Subtle animated background elements */}
              <div className="absolute inset-0 z-0 pointer-events-none">
                <div className={`absolute w-64 h-64 bg-thrysec-blue rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob top-10 left-1/4`}></div> {/* Adjusted for logo blue, increased opacity */}
                <div className={`absolute w-64 h-64 bg-thrysec-orange rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000 bottom-10 right-1/4`}></div> {/* Adjusted for logo orange, increased opacity */}
                <div className={`absolute w-64 h-64 bg-thrysec-blue rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2`}></div> {/* Adjusted for logo blue, increased opacity */}
              </div>

              <div className="z-10 max-w-4xl mx-auto px-4 py-8 relative"> {/* Added relative for z-index context */}
                {/* Updated H1 structure for two lines with typewriter effect */}
                <h1 className="text-3xl sm:text-4xl md:text-7xl font-extrabold leading-tight mb-6 drop-shadow-lg flex flex-col items-center">
                  <TypewriterText text="Proactive Defense." delay={70} />
                  <TypewriterText text="Rapid Response." delay={70} startDelay={2000} /> {/* Start after 2 seconds */}
                </h1>
                <p className="text-base sm:text-lg md:text-2xl text-slate-700 mb-10 drop-shadow-md"> {/* Adjusted for white theme */}
                  24/7 Monitoring, Threat Detection, and Rapid Incident Response to Protect Your Business.
                </p>
                {/* Updated href to Calendly link */}
                <a href="https://calendly.com/thrysec" target="_blank" rel="noopener noreferrer" className={`bg-gradient-to-r from-thrysec-blue to-thrysec-orange hover:from-thrysec-blue hover:to-thrysec-orange text-white font-bold py-3 px-6 md:py-4 md:px-8 rounded-full shadow-lg transition duration-300 ease-in-out transform hover:scale-105 hover:shadow-xl text-base md:text-lg border border-thrysec-blue focus:outline-none focus:ring-2 focus:ring-thrysec-blue focus:ring-offset-2 focus:ring-offset-gray-50`}>
                  Get a Free Consultation
                </a>
              </div>
            </section>

            {/* Services Section */}
            <section id="services" className="py-20 px-6 bg-gray-100 relative overflow-hidden section-pattern"> {/* Adjusted for white theme */}
                <div className="container mx-auto text-center relative z-10">
                    <h2 className="text-3xl md:text-4xl font-bold mb-12">
                        <GradientText>Our Comprehensive SOC Services</GradientText>
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                        {/* Service Card 1 */}
                        <div className="bg-white p-8 rounded-xl shadow-2xl border border-gray-200 group hover:shadow-thrysec-blue/20 transition duration-300 ease-in-out transform hover:-translate-y-2 relative overflow-hidden"> {/* Adjusted for white theme */}
                            <div className="absolute inset-0 rounded-xl border-2 border-transparent group-hover:border-thrysec-blue group-hover:border-opacity-50 transition-all duration-300 ease-in-out"></div> {/* Adjusted for white theme */}
                            <div className={`text-thrysec-blue mb-6 group-hover:text-thrysec-orange transition duration-300`}> {/* Adjusted for logo colors */}
                                <svg className="h-14 w-14 md:h-16 md:w-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.001 12.001 0 002 12c0 2.757 1.125 5.228 2.937 7.073A11.999 11.999 0 0012 22c2.928 0 5.618-1.191 7.663-3.127A11.997 11.997 0 0022 12c0-2.757-1.125-5.228-2.937-7.073z"></path></svg>
                            </div>
                            <h3 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">Advanced Threat Analytics
</h3> {/* Adjusted for white theme */}
                            <p className="text-slate-700 text-base"> {/* Adjusted for white theme */}
                                Proactively monitor and analyze network activity and system behavior to detect sophisticated and hidden threats.
                            </p>
                        </div>

                        {/* Service Card 2 */}
                        <div className="bg-white p-8 rounded-xl shadow-2xl border border-gray-200 group hover:shadow-thrysec-blue/20 transition duration-300 ease-in-out transform hover:-translate-y-2 relative overflow-hidden"> {/* Adjusted for white theme */}
                            <div className="absolute inset-0 rounded-xl border-2 border-transparent group-hover:border-thrysec-blue group-hover:border-opacity-50 transition-all duration-300 ease-in-out"></div> {/* Adjusted for white theme */}
                            <div className={`text-thrysec-blue mb-6 group-hover:text-thrysec-orange transition duration-300`}> {/* Adjusted for logo colors */}
                                <svg className="h-14 w-14 md:h-16 md:w-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
                            </div>
                            <h3 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">Vulnerability Management</h3> {/* Adjusted for white theme */}
                            <p className="text-slate-700 text-base"> {/* Adjusted for white theme */}
                              Continuously identify, assess, and prioritize weaknesses in your systems, applications, and networks with ongoing scanning.
                            </p>
                        </div>

                        {/* Service Card 3 */}
                        <div className="bg-white p-8 rounded-xl shadow-2xl border border-gray-200 group hover:shadow-thrysec-blue/20 transition duration-300 ease-in-out transform hover:-translate-y-2 relative overflow-hidden"> {/* Adjusted for white theme */}
                            <div className="absolute inset-0 rounded-xl border-2 border-transparent group-hover:border-thrysec-blue group-hover:border-opacity-50 transition-all duration-300 ease-in-out"></div> {/* Adjusted for white theme */}
                            <div className={`text-thrysec-blue mb-6 group-hover:text-thrysec-orange transition duration-300`}> {/* Adjusted for logo colors */}
                                <svg className="h-14 w-14 md:h-16 md:w-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                            </div>
                            <h3 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">Penetration Testing</h3> {/* Adjusted for white theme */}
                            <p className="text-slate-700 text-base"> {/* Adjusted for white theme */}
                              Proactive, authorized simulations of cyberattacks to expose and report on your security weaknesses.
                            </p>
                        </div>

                        {/* Service Card 4 */}
                        <div className="bg-white p-8 rounded-xl shadow-2xl border border-gray-200 group hover:shadow-thrysec-blue/20 transition duration-300 ease-in-out transform hover:-translate-y-2 relative overflow-hidden"> {/* Adjusted for white theme */}
                            <div className="absolute inset-0 rounded-xl border-2 border-transparent group-hover:border-thrysec-blue group-hover:border-opacity-50 transition-all duration-300 ease-in-out"></div> {/* Adjusted for white theme */}
                            <div className={`text-thrysec-blue mb-6 group-hover:text-thrysec-orange transition duration-300`}> {/* Adjusted for logo colors */}
                                <svg className="h-14 w-14 md:h-16 md:w-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path></svg>
                            </div>
                            <h3 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">Incident Response & Forensics
</h3> {/* Adjusted for white theme */}
                            <p className="text-slate-700 text-base"> {/* Adjusted for white theme */}
                               Rapid containment, investigation, and recovery from security breaches, with meticulous digital evidence analysis.
                            </p>
                        </div>

                        {/* Service Card 5 */}
                        <div className="bg-white p-8 rounded-xl shadow-2xl border border-gray-200 group hover:shadow-thrysec-blue/20 transition duration-300 ease-in-out transform hover:-translate-y-2 relative overflow-hidden"> {/* Adjusted for white theme */}
                            <div className="absolute inset-0 rounded-xl border-2 border-transparent group-hover:border-thrysec-blue group-hover:border-opacity-50 transition-all duration-300 ease-in-out"></div> {/* Adjusted for white theme */}
                            <div className={`text-thrysec-blue mb-6 group-hover:text-thrysec-orange transition duration-300`}> {/* Adjusted for logo colors */}
                                <svg className="h-14 w-14 md:h-16 md:w-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"></path></svg>
                            </div>
                            <h3 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">Cloud Security Monitoring</h3> {/* Adjusted for white theme */}
                            <p className="text-slate-700 text-base"> {/* Adjusted for white theme */}
                               Continuous oversight of your cloud environment, tracking configurations, user activity, and traffic for compliance and threat detection.
                            </p>
                        </div>

                        {/* Service Card 6 */}
                        <div className="bg-white p-8 rounded-xl shadow-2xl border border-gray-200 group hover:shadow-thrysec-blue/20 transition duration-300 ease-in-out transform hover:-translate-y-2 relative overflow-hidden"> {/* Adjusted for white theme */}
                            <div className="absolute inset-0 rounded-xl border-2 border-transparent group-hover:border-thrysec-blue group-hover:border-opacity-50 transition-all duration-300 ease-in-out"></div> {/* Adjusted for white theme */}
                            <div className={`text-thrysec-blue mb-6 group-hover:text-thrysec-orange transition duration-300`}> {/* Adjusted for logo colors */}
                                <svg className="h-14 w-14 md:h-16 md:w-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                            </div>
                            <h3 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">Cyber Threat Intelligence (CTI)</h3> {/* Adjusted for white theme */}
                            <p className="text-slate-700 text-base"> {/* Adjusted for white theme */}
                               Stay ahead of attackers by gathering and analyzing data on current and emerging threats to build stronger defenses.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* New Section: Our Unique Approach */}
            <section id="approach" className="py-20 px-6 bg-gray-50 relative overflow-hidden section-pattern"> {/* Adjusted for white theme */}
              <div className="container mx-auto text-center relative z-10">
                <h2 className="text-3xl md:text-4xl font-bold mb-12">
                  <GradientText>Our Unique Approach</GradientText>
                </h2>
                <div className="max-w-4xl mx-auto text-slate-800 text-lg md:text-xl leading-relaxed"> {/* Adjusted for white theme */}
                  <p className="mb-6 text-center">
                    At ThrySec, we believe in a proactive and adaptive cybersecurity strategy. Our approach integrates cutting-edge technology with human expertise to provide a comprehensive defense against evolving threats. We don't just react; we anticipate, detect, and neutralize.
                  </p>
                  <ul className="list-disc list-inside text-left mx-auto max-w-xl space-y-2">
                    <li><strong className={`text-thrysec-blue`}>Proactive Threat Hunting:</strong> Actively searching for threats that evade traditional security measures.</li> {/* Adjusted for logo blue */}
                    <li><strong className={`text-thrysec-blue`}>Rapid Incident Response:</strong> Minimizing impact with swift and effective containment and remediation.</li> {/* Adjusted for logo blue */}
                    <li><strong className={`text-thrysec-blue`}>Continuous Improvement:</strong> Adapting our strategies based on the latest threat intelligence and vulnerability research.</li> {/* Adjusted for logo blue */}
                  </ul>
                  <p className="mt-6 text-center">
                    Your security is our mission. We're committed to building resilient defenses that protect your critical assets 24/7.
                  </p>
                </div>
              </div>
            </section>


            {/* About Us Section */}
            <section id="about" className="py-20 px-6 bg-gray-100 relative overflow-hidden section-pattern"> {/* Adjusted for white theme */}
                <div className="container mx-auto text-center max-w-3xl relative z-10">
                    <h2 className="text-3xl md:text-4xl font-bold mb-8">
                        <GradientText>About ThrySec</GradientText>
                    </h2>
                    <p className="text-base md:text-lg text-slate-800 leading-relaxed mb-6 text-center"> {/* Adjusted for white theme */}
                        ThrySec is a leading provider of Security Operations Center (SOC) services, dedicated to safeguarding businesses from the ever-evolving landscape of cyber threats. With a commitment to excellence and a proactive approach, we empower organizations to focus on their core objectives while we handle their cybersecurity needs.
                    </p>
                    <p className="text-base md:text-lg text-slate-800 leading-relaxed text-center"> {/* Adjusted for white theme */}
                        Our mission is to deliver unparalleled security intelligence and rapid response capabilities, ensuring the integrity, confidentiality, and availability of your critical assets.
                    </p>
                </div>
            </section>

            {/* Contact Section */}
            <section id="contact" className="py-20 px-6 bg-gray-50 relative overflow-hidden section-pattern"> {/* Adjusted for white theme */}
                <div className="container mx-auto text-center max-w-2xl relative z-10">
                    <h2 className="text-3xl md:text-4xl font-bold mb-12">
                        <GradientText>Get in Touch</GradientText>
                    </h2>
                    <p className="text-base md:text-lg text-slate-800 mb-8 text-center"> {/* Adjusted for white theme */}
                        Ready to enhance your cybersecurity posture? Schedule a meeting with our experts today.
                    </p>
                    {/* Replaced form with a direct link to Calendly */}
                    <a
                      href="https://calendly.com/thrysec" // Placeholder Calendly link
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`bg-gradient-to-r from-thrysec-blue to-thrysec-orange hover:from-thrysec-blue hover:to-thrysec-orange text-white font-bold py-3 px-6 md:py-4 md:px-8 rounded-full shadow-lg transition duration-300 ease-in-out transform hover:scale-105 hover:shadow-xl text-base md:text-lg border border-thrysec-blue focus:outline-none focus:ring-2 focus:ring-thrysec-blue focus:ring-offset-2 focus:ring-offset-gray-50`}
                    >
                      Schedule a Meeting
                    </a>
                    <p className="text-sm sm:text-base md:text-lg text-slate-800 mt-8 text-center"> {/* Adjusted for white theme */}
                        For service-related queries, you can also email us directly at <a href="mailto:security@thrysec.com" className={`text-thrysec-blue hover:underline`}>security@thrysec.com</a>. {/* Adjusted for white theme */}
                    </p>
                </div>
            </section>
          </>
        )}

        {currentPage === 'privacy' && <PrivacyPolicyPage />}
        {currentPage === 'terms' && <TermsOfServicePage />}
      </main>

      {/* Footer */}
      <footer className="bg-white py-8 px-6 text-center text-slate-600 rounded-t-xl border-t border-blue-200"> {/* Adjusted for white theme */}
        <div className="container mx-auto">
          <p className="text-sm md:text-base">&copy; 2025 ThrySec. All rights reserved.</p>
          <div className="flex justify-center space-x-4 md:space-x-6 mt-4 text-sm md:text-base">
            <a href="#" onClick={() => navigateTo('privacy')} className="hover:text-slate-900 transition duration-300">Privacy Policy</a> {/* Adjusted for white theme */}
            <a href="#" onClick={() => navigateTo('terms')} className="hover:text-slate-900 transition duration-300">Terms of Service</a> {/* Adjusted for white theme */}
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
